import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { logToAmplitude } from '../../helpers/logAmplitudeEvent';
import useMedia from 'hooks/useMedia';

import Anchor from 'components/base/Anchor/Anchor';
import Box from 'components/elements/Box';
import H1 from 'components/elements/h1';
import Span from 'components/elements/span';
import WarnerFooter from './WarnerFooter';

import {
  ColorfulLink,
  ColumnBox,
  FooterBannerBox,
  FooterFrame,
  Logo,
  StyledFooter,
} from './styles';

import colors from 'styles/themes/colors';
import { RootState } from 'redux/rootReducer';
import { IAuthenticationProfile } from 'redux/reducers/authentication';
import checkBetaGroup from 'helpers/checkBetaGroup';

const CategoryTitle = ({
  fontSize,
  title,
}: {
  fontSize: string;
  title: string;
}): JSX.Element => (
  <Span
    pb="0.75rem"
    color={colors.MediumGrey}
    fontSize={fontSize}
    textTransform="uppercase"
    letterSpacing="1px"
  >
    {title}
  </Span>
);

const GetAccessOpusLink = ({ isSmall }: { isSmall?: boolean }): JSX.Element => (
  <ColorfulLink
    isSmall={isSmall}
    onClick={(): void => logToAmplitude('Get Opus Access Clicked')}
    href="https://warnermusicgroup.freshservice.com/support/catalog/items/117"
    target="_blank"
    rel="noopener noreferrer"
  >
    Get Opus Access
  </ColorfulLink>
);

const FooterBanner = ({
  isExternalUser,
  isSmallScreen,
}: {
  isExternalUser: boolean;
  isSmallScreen: boolean;
}): JSX.Element => (
  <FooterBannerBox
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    py={6}
    backgroundColor={colors.White}
  >
    <H1 as="h3">Request an Opus Account</H1>
    <Span
      px={isSmallScreen ? '3rem' : 0}
      fontSize="0.875rem"
      textAlign="center"
    >
      {isExternalUser
        ? 'Please contact your WMG contact to request additional access'
        : 'Get access to the OPUS Application for an internal or external (non-WMG) user'}
    </Span>

    {!isExternalUser && <GetAccessOpusLink isSmall />}
  </FooterBannerBox>
);

const Footer: React.FC<{
  authenticationProfile: IAuthenticationProfile;
  BetaGroupPriorityPlaylist: string[];
  isAccessBannerShown: boolean;
  isAuthenticated: boolean;
  isExternalUser: boolean;
  isLoadedAccessBanner: boolean;
  isOpusNotifierOn?: boolean;
}> = ({
  authenticationProfile,
  BetaGroupPriorityPlaylist,
  isAccessBannerShown,
  isAuthenticated,
  isExternalUser,
  isLoadedAccessBanner,
  isOpusNotifierOn,
}) => {
  const isSmallScreen = useMedia('(max-width: 66rem)');

  const showOpusNotifier =
    isOpusNotifierOn &&
    checkBetaGroup(BetaGroupPriorityPlaylist, authenticationProfile?.email);

  return (
    <>
      {isAuthenticated ? (
        <FooterFrame data-pw="footer">
          {isLoadedAccessBanner &&
            (isAccessBannerShown === undefined ||
              isAccessBannerShown === null) && (
              <FooterBanner
                isExternalUser={isExternalUser}
                isSmallScreen={isSmallScreen}
              />
            )}

          <Box
            display="flex"
            flexDirection={isSmallScreen ? 'column' : 'row'}
            width={isSmallScreen ? 'auto' : '78.2%'}
            margin="0 auto"
            pt="3.5rem"
            pb="3.5rem"
            color={colors.White}
            textAlign={isSmallScreen ? 'center' : 'initial'}
          >
            <ColumnBox pt={3} isSmallScreen={isSmallScreen}>
              <Logo src="/images/icon-opus-logo-white.svg" alt="Opus" />
              <Span fontSize="0.875rem">© 2022 Warner Music Inc.</Span>
              {!isExternalUser && (
                <GetAccessOpusLink isSmall={!isSmallScreen} />
              )}
            </ColumnBox>

            <ColumnBox isSmallScreen={isSmallScreen}>
              <CategoryTitle
                title="Resources"
                fontSize={isSmallScreen ? '0.875rem' : '0.75rem'}
              />
              {!isExternalUser ? (
                <>
                  <Anchor to="/trainingVideo" target="_blank">
                    Training Video
                  </Anchor>

                  <a
                    href="https://wmg.sharepoint.com/sites/Opus/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FOpus%2FShared%20Documents%2FOpus%20Course%201%20%2D%20Opus%20Basics%2Epdf&parent=%2Fsites%2FOpus%2FShared%20Documents"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Opus Basics
                  </a>
                </>
              ) : null}

              <Anchor to="/glossary">Glossary</Anchor>
              <Anchor to="/faq">FAQ</Anchor>
              {showOpusNotifier && (
                <Anchor to="/downloadOpusNotifierApp">
                  Interested in Mobile App?
                </Anchor>
              )}
            </ColumnBox>

            <ColumnBox isSmallScreen={isSmallScreen}>
              <CategoryTitle
                title="Need Help?"
                fontSize={isSmallScreen ? '0.875rem' : '0.75rem'}
              />
              <Anchor to="/glossary/general-information/contact">
                Contact Us
              </Anchor>
            </ColumnBox>

            <ColumnBox isSmallScreen={isSmallScreen}>
              <CategoryTitle
                title="Opus team"
                fontSize={isSmallScreen ? '0.875rem' : '0.75rem'}
              />
              <Anchor to="/glossary/general-information/about-opus">
                About Opus
              </Anchor>
            </ColumnBox>

            <ColumnBox isSmallScreen={isSmallScreen}>
              <CategoryTitle
                title="Legal"
                fontSize={isSmallScreen ? '0.875rem' : '0.75rem'}
              />
              <a
                href="https://privacy.wmg.com/ecom/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              <a
                href="https://www.wmg.com/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              <a
                href="https://www.wminewmedia.com/cookies-policy/en/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookies Policy
              </a>

              {/* eslint-disable */}
              <a className="ot-sdk-show-settings">Cookies Settings</a>
            </ColumnBox>
          </Box>
        </FooterFrame>
      ) : (
        <StyledFooter role="contentinfo" data-pw="footer">
          <WarnerFooter />
        </StyledFooter>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState): any => ({
  authenticationProfile: state.authentication?.profile,
  BetaGroupPriorityPlaylist:
    state.consulEnvData?.data?.BetaGroupPriorityPlaylist,
  isAccessBannerShown: state.userSettings?.data?.opusAccessFooterBanner,
  isLoadedAccessBanner:
    state.userSettings?.loading?.all === false &&
    state.userSettings?.success?.all === true,
  isAuthenticated: state.authentication.isAuthenticated,
  isExternalUser: state.authentication.isExternalUser,
  isOpusNotifierOn: state.consulEnvData?.data?.isOpusNotifierOn,
});
/** We have to wrap the Footer in withRouter so it bypasses connects automatic should component update functionality */
export default withRouter(connect(mapStateToProps)(Footer as any) as any);
